import { OMenuItem } from 'lib/omnibar/types';
import { HOMEPAGE_ID, SPACE_ID, SEARCH_TYPE, BASE_URL } from 'lib/config';
import { conststripHtml, hrefTitle } from 'lib/utils';
import {
  PageContent,
  Ancestor,
  Child,
  Label,
  SearchResult,
  SearchResults,
  SanitizedResult,
} from './types';

class ConfluenceAPI {
  homePageId: string;

  spaceId: string;

  searchType: string;

  constructor() {
    this.homePageId = HOMEPAGE_ID;
    this.spaceId = SPACE_ID;
    this.searchType = SEARCH_TYPE;
  }

  async getNavigationItems(pageId?: string | undefined): Promise<OMenuItem[]> {
    const data = await this.getPageContent(pageId, 'children.content', true);

    if (!data) return [];

    const menuIconMap = (
      menuItemId: string,
      labels?: Label[]
    ): string | undefined => {
      if (menuItemId === this.homePageId) {
        return 'places-home-1';
      }
      if (labels?.[0]?.name.startsWith('icon-')) {
        return labels[0].name.replace('icon-', '');
      }
    };

    const getItems = (content: Child[]): OMenuItem[] => {
      return content.map(({ children, id, labels, title }) => {
        const item = {
          icon: menuIconMap(id, labels?.label),
          pageId: id,
          title,
          url: `/article/${id}/${hrefTitle(title)}`,
        } as OMenuItem;

        if (children?.content) {
          item.items = getItems(children.content);
        }

        return item;
      });
    };

    const homeItem = {
      icon: menuIconMap(this.homePageId),
      pageId: this.homePageId,
      title: 'Home',
      url: '/',
    } as OMenuItem;

    return [homeItem].concat(getItems(data.children.content));
  }

  async getPageContent(
    pageId?: string,
    expand = 'ancestors,body.view,version',
    usePrototype = false
  ): Promise<PageContent> {
    const url = new URL(`${BASE_URL}/page/${pageId || this.homePageId}`);

    if (usePrototype) {
      url.searchParams.append('prototype', '1');
    }
    url.searchParams.append('expand', expand);

    const shell = document.querySelector('omnibar-shell');

    if (shell && pageId) shell.pageId = pageId;

    return fetch(url.href)
      .then((response) => response.json())
      .then((response) => {
        // accounts for the formats of both APIs
        const lastModifiedDate = {
          ...response.lastModifiedDate,
          ...response.version,
        };

        lastModifiedDate.friendly =
          lastModifiedDate.friendly || lastModifiedDate.friendlyWhen;

        const { ancestors } = response;

        let breadcrumbs = [];

        if (ancestors) {
          ancestors.shift();

          breadcrumbs = ancestors.map(({ id, title }: Ancestor) => ({
            key: id,
            title,
            url: `/article/${id}/${hrefTitle(title)}`,
          }));

          breadcrumbs.push({
            key: response.id,
            title: response.title,
            url: `/article/${response.id}`,
          });
        }

        const body = response.body.value || response.body.view.value;
        const linkRegex = /(\/wiki\/spaces\/SHS\/pages\/)(\d+)([^"]+)/g;
        const idRegex = /(id=")([^"]*)/g;

        const htmlWithClasses = body
          .replaceAll('<h1 ', '<h1 class="r-heading-xl r-pt-2"')
          .replaceAll('<h2 ', '<h2 class="r-heading-xl r-pt-2"')
          .replaceAll('<h3 ', '<h3 class="r-heading-lg" ')
          .replaceAll('<h4 ', '<h4 class="r-heading-md" ')
          .replaceAll('<h5 ', '<h5 class="r-heading-md" ')
          .replaceAll('<a ', '<a class="r-text-primary" ')
          .replaceAll('<p>', '<p class="r-mb-4">')
          .replaceAll('<ol>', '<ol class="r-list-decimal r-pl-8 r-mb-12">')
          .replaceAll('<ul>', '<ul class="r-list-disc r-pl-8 r-mb-5">')
          .replaceAll('<li>', '<li class="r-mb-2">')
          .replaceAll('confluenceTable', 'r-mt-5 r-table')
          .replaceAll('<tr>', '<tr class="r-table-row">')
          .replaceAll('confluenceTd', 'r-table-cell')
          .replaceAll('confluence-embedded-image', 'r-my-6')
          .replaceAll('image-center', 'r-m-auto')
          .replaceAll('youtube-player', 'youtube-player r-my-6')
          .replaceAll(linkRegex, '/article/$2')
          .replaceAll(idRegex, (string: string, $1: string, $2: string) => {
            return `id="${$2.replace(/\W/g, '')}"`;
          });

        const excerpt = `${conststripHtml(body).substring(0, 150)}...`;

        return {
          ...response,
          body: htmlWithClasses,
          breadcrumbs,
          excerpt,
          lastModifiedDate,
        } as PageContent;
      });
  }

  async search(
    searchTerm: string,
    cursor?: string,
    start = 0,
    limit = 1
  ): Promise<SearchResults> {
    const buildSearchURL = (): URL => {
      const searchUrl = new URL(`${BASE_URL}/search`);

      if (cursor) {
        searchUrl.searchParams.append('cursor', cursor);
      }

      searchUrl.searchParams.append('term', searchTerm);
      searchUrl.searchParams.append('space', this.spaceId);
      searchUrl.searchParams.append('type', this.searchType);
      searchUrl.searchParams.append('start', `${start}`);
      searchUrl.searchParams.append('limit', `${limit}`);

      return searchUrl;
    };

    const url = buildSearchURL();

    const searchResult = (await fetch(url.href).then((response) =>
      response.json()
    )) as SearchResult;

    if (!searchResult.results) return {} as SearchResults;

    const {
      /* eslint-disable-next-line @typescript-eslint/naming-convention */
      _links: links,
      limit: currentLimit,
      start: currentStart,
      totalSize,
    } = searchResult;

    const currentPage = Math.floor(currentStart / currentLimit) + 1;
    const nextStart = currentStart + currentLimit;
    const nextLimit = Math.ceil((nextStart % totalSize) / currentPage);

    let nextCursor: string | null = null;

    if (links?.next) {
      const linkRegex = /(cursor=)(.*)(%3D)/;
      const match = links.next.match(linkRegex);

      if (match && match?.length >= 3) {
        /* eslint prefer-destructuring: ["error", {VariableDeclarator: {object: true}}] */
        nextCursor = match[2];
      }
    }

    const sanitizeHighlight = (text: string) => {
      // The API returns the searchTerms warpped in these @@@h1@@@ tags.
      // We are replacing them with <strong> tags so that we can wrap it.
      return text
        .replaceAll('@@@hl@@@', '<strong class="r-text-primary">')
        .replaceAll('@@@endhl@@@', '</strong>');
    };

    return {
      cursor: nextCursor,
      limit: nextLimit,
      results: searchResult.results.map(
        (r) =>
          ({
            excerpt: sanitizeHighlight(r.excerpt),
            friendlyLastModified: r.friendlyLastModified,
            id: r.content.id,
            title: sanitizeHighlight(r.title),
          } as SanitizedResult)
      ),
      showingCount: start + limit,
      start: nextStart,
      totalCount: totalSize,
    } as SearchResults;
  }
}

export default ConfluenceAPI;
